import DOMPurify from 'dompurify';
import { getDocumentKeyMetricValue } from '../../../get-document-key-metric-value/index.js';
import { getDocumentSourceImageUrl } from '../../../get-document-source-image-url/index.js';
import { GydaDocument } from '../../../gyda-document';
import {
    CUSTOM_KEY_METRIC,
    DEFAULT_KEY_METRIC,
    KeyMetric,
    KeyMetricTranslations
} from '../../../key-metric';
import { logger } from '../../../logger.js';
import { DocumentListItemMetadata } from './metadata';
import { PREMIUM_DOCUMENT_EVENT_NAME } from '../../../premium-document-handler/index.js';
import { TranslationComponent } from '../../../translations/translation-component.js';
import { version } from '../../../version';
import { MediaTypes } from '../../../media-type-translation/index.js';

const CLEAN_TWITTER_OPERATORS_MATCH = /^(RT|QT)\s+?/;

const [ MAJOR_VERSION ] = version.split('.');

const NO_DOCUMENT = Symbol('NO DOCUMENT SET');
const NO_KEY = Symbol('NO KEY SET');
const NO_VALUE = Symbol('NO VALUE SET');

const SOURCE_ID_TWITTER = 'social:twitter';

function isHostedContentProvider(document) {
    if (document.disableInAppOpening) {
        return false;
    }

    return document.isHosted === true || document.isSharingDisabled === true;
}

/**
 * This is the internal document list item used by other visualizations.
 * It is intended to be adapted to match the MWV API and made public as a
 * visualization export.
 * @private
 */
export class DocumentListItem extends HTMLElement {
    #companyId;
    #container;
    #document = NO_DOCUMENT;
    #documentUrl;
    #key = NO_KEY;
    #keyMetric = DEFAULT_KEY_METRIC;
    #keyMetricLabel = '';
    #language = 'en';
    #mediaIcon = '';
    #onPointClick = null;
    #title = '';
    #value = NO_VALUE;

    static get tagName () {
        return `ua-applet-document-list-item-v${MAJOR_VERSION}`;
    }

    constructor () {
        super();
        this.attachShadow({ mode: 'open' });

        this.shadowRoot.innerHTML = /*html*/`
            <style>
                :host {
                    font-family: var(--flux-font-family-base);
                }

                article {
                    align-items: center;
                    align-self: stretch;
                    display: flex;
                    gap: 8px;
                    padding: 4px 8px;
                }

                .source-avatar {
                    height: 36px;
                    width: 36px;
                    flex-shrink: 0;
                }

                .details {
                    color: var(--flux-color-black-87);
                    display: grid;
                    flex: 1;
                    width: calc(100% - 44px);
                }

                .title {
                    cursor: pointer;
                    font-size: 12px;
                    font-weight: 700;
                    line-height: 16px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    word-break: break-word;

                    &:hover {
                        text-decoration: underline;
                    }

                    & a {
                        color: var(--flux-color-black-87);
                        text-decoration: none;
                    }
                }

                .footer {
                    color: var(--flux-color-black-54-solid);
                    display: flex;
                    font-size: 12px;
                    font-weight: var(--flux-font-weight-medium);
                    justify-content: space-between;
                    line-height: 16px;
                }
            </style>
            <article></article>
        `;

        this.#container = this.shadowRoot.querySelector('article');
    }

    #getDocumentValue(keyMetric) {
        return getDocumentKeyMetricValue({
            document: this.#document,
            keyMetric,
            language: this.#language
        });
    }

    get #keyMetricHtml() {
        let interiorHTML;
        const keyMetric = this.#keyMetric !== DEFAULT_KEY_METRIC ? this.#keyMetric : KeyMetric.reach;

        try {
            const label = keyMetric !== CUSTOM_KEY_METRIC
                ? `<${TranslationComponent.tagName} key="${KeyMetricTranslations[keyMetric]}" language="${this.#language}"></${TranslationComponent.tagName}>`
                : DOMPurify.sanitize(this.#keyMetricLabel);

            const value = keyMetric !== CUSTOM_KEY_METRIC
                ? this.#getDocumentValue(keyMetric)
                : this.#value;

            interiorHTML = typeof value === 'string'
                ? value
                : `<label class="key-metric">
                      <flux-truncated-number number="${value}"></flux-truncated-number>
                      ${label}
                  </label>`;
        }
        catch (error) {
            logger.error('Error rendering document', error.message, error.stack);
            interiorHTML = '—';
        }

        return /*html*/`
            <span id="key-metric" class="metric-container">
                ${interiorHTML}
            </span>`;
    }

    /**
     * @returns {undefined}
     */
    setData({
        companyId,
        document,
        key,
        keyMetric,
        keyMetricLabel,
        language,
        onPointClick = null,
        value
    }) {
        this.#companyId = companyId;
        this.#document = document ? new GydaDocument(document) : NO_DOCUMENT;
        this.#key = key ?? NO_KEY;
        this.#keyMetric = keyMetric ?? DEFAULT_KEY_METRIC;
        this.#keyMetricLabel = keyMetricLabel;
        this.#language = language;
        this.#onPointClick = onPointClick;
        this.#value = value ?? NO_VALUE;

        this.#mediaIcon = this.#document.mediaType === 'twitter' ? 'x' : this.#document.mediaType;

        this.#title = this.#document.title || this.#key;
        if (this.#key !== NO_KEY && this.#document.sourceId === SOURCE_ID_TWITTER) {
            this.#title = this.#key.replace(CLEAN_TWITTER_OPERATORS_MATCH, '');
        }

        if (this.#key !== NO_KEY && this.#document.mediaType === MediaTypes.facebook) {
            this.#title = this.#document.body;
        }

        if (this.#onPointClick === null) {
            this.#documentUrl = this.#document.paywallUrl || this.#document.url;
            if (isHostedContentProvider(this.#document)) {
                const sharedDocumentViewerUrl = location.hostname.includes('meltwater.net')
                    ? new URL(`https://staging.meltwater.net/api/ucs/shareddocumentviewer/${this.#document.documentId}`)
                    : new URL(`https://app.meltwater.com/api/ucs/shareddocumentviewer/${this.#document.documentId}`);

                sharedDocumentViewerUrl.searchParams.set('companyId', this.#companyId);
                sharedDocumentViewerUrl.searchParams.set('productType', 'universal-analytics');
                this.#documentUrl = sharedDocumentViewerUrl.toString();
            }
        }

        this.#render();
    }

    #render () {
        if (this.#document === NO_DOCUMENT) {
            this.style.visibility = 'hidden';
        }
        else {
            this.style.visibility = 'visible';

            this.#container.innerHTML = /*html*/`
                <flux-avatar
                    class="source-avatar"
                    image-url="${getDocumentSourceImageUrl(this.#document)}"
                    media-icon="${this.#mediaIcon}"
                    name="${this.#document.source}"
                >
                </flux-avatar>
                <div class="details">
                    <div class="title">
                        <a target="_blank" href="${this.#documentUrl}">
                            ${DOMPurify.sanitize(this.#title, {ALLOWED_TAGS: ['em']})}
                        </a>
                    </div>

                    <${DocumentListItemMetadata.tagName}></${DocumentListItemMetadata.tagName}>

                    <div class="footer">
                        <span id="measure-value">
                            ${this.#keyMetricHtml}
                        </span>

                        <flux-content-card-sentiment-selector class="sentiment" documentId="${this.#document.documentId}" sentiment="${this.#document.sentiment}" show-dropdown="false">
                        </flux-content-card-sentiment-selector>
                    </div>
                </div>
            `;

            this.shadowRoot.querySelector(DocumentListItemMetadata.tagName).document = this.#document;

            this.shadowRoot.querySelector('.title').addEventListener('click', (event) => {
                if (this.#onPointClick !== null) {
                    event.preventDefault();
                    event.stopPropagation();

                    this.#onPointClick({
                        documentId: this.#document.documentId,
                        key: this.#key
                    });
                } else if (isHostedContentProvider(this.#document)) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.dispatchEvent(new CustomEvent(PREMIUM_DOCUMENT_EVENT_NAME, {
                        bubbles: true,
                        composed: true,
                        cancelable: true,
                        detail: {
                            documentId: this.#document.documentId,
                            document: this.#document
                        }
                    }));
                }
            });
        }
    }
}

if (!customElements.get(DocumentListItem.tagName)) {
    customElements.define(DocumentListItem.tagName, DocumentListItem);
}
